const galleries = new WeakMap();
const heroImages = new WeakSet();
let activeGallery;

export function shopifyEventMediaUrl(value, base) {
  if (typeof value !== 'string' || !value.trim()) return null;
  try {
    const url = new URL(value, base);
    const origin = new URL(base).origin;
    if (!/^https?:$/.test(url.protocol)) return null;
    if (url.origin !== origin && url.hostname !== 'cdn.shopify.com' && url.hostname !== 'cdn.shopifycdn.net') return null;
    return url.href;
  } catch { return null; }
}

export function mountFillBagGallery(section, environment = {}) {
  if (galleries.has(section)) return galleries.get(section);
  const doc = environment.document || section.ownerDocument;
  const win = environment.window || doc.defaultView;
  const track = section.querySelector('[data-fab-track]');
  const dialog = section.querySelector('[data-fab-dialog]');
  const content = section.querySelector('[data-fab-content]');
  if (!track || !dialog || !content) return null;
  const previous = section.querySelector('[data-fab-prev]');
  const next = section.querySelector('[data-fab-next]');
  const closeButton = section.querySelector('[data-fab-close]');
  const error = section.querySelector('[data-fab-error]');
  const fallback = section.querySelector('[data-fab-fallback]');
  const controller = new (win.AbortController || AbortController)();
  const listen = (target, name, handler, options = {}) => target?.addEventListener(name, handler, { ...options, signal: controller.signal });
  const reducedMotion = win.matchMedia?.('(prefers-reduced-motion: reduce)') || { matches: false };
  let player, playerError, trigger, previousOverflow = '', opened = false, destroyed = false;
  let drag, suppressClick = false, clickTimer;
  const maximum = () => Math.max(0, track.scrollWidth - track.clientWidth);
  const updateArrows = () => {
    if (previous) previous.disabled = track.scrollLeft <= 2;
    if (next) next.disabled = track.scrollLeft >= maximum() - 2;
  };
  const move = direction => track.scrollBy({ left: direction * Math.max(170, track.clientWidth * .78), behavior: reducedMotion.matches ? 'auto' : 'smooth' });
  function releasePlayer() {
    if (player) {
      player.removeEventListener('error', playerError);
      try { player.pause(); } catch { /* A failed resource may not expose playback state. */ }
      player.removeAttribute('src');
      player.querySelectorAll?.('source').forEach(source => source.removeAttribute('src'));
      try { player.load(); } catch { /* Release remains safe after a failed load. */ }
      player.remove(); player = undefined; playerError = undefined;
    }
    content.replaceChildren();
    if (error) error.hidden = true;
    fallback?.removeAttribute('href');
  }
  function finishClose() {
    if (!opened) return;
    opened = false;
    releasePlayer();
    trigger?.setAttribute('aria-expanded', 'false');
    const anotherDialog = doc.querySelector('dialog[open]');
    doc.body.style.overflow = anotherDialog ? 'hidden' : previousOverflow;
    if (!anotherDialog && trigger?.isConnected) trigger.focus({ preventScroll: true });
    if (activeGallery === api) activeGallery = undefined;
  }
  function close() {
    if (dialog.open) dialog.close();
    finishClose();
  }
  function open(link) {
    const src = shopifyEventMediaUrl(link?.dataset.fabSrc, win.location.href);
    if (!src || destroyed) return false;
    if (activeGallery && activeGallery !== api) activeGallery.close();
    if (opened) close();
    const openDialogs = [...doc.querySelectorAll('dialog[open]')];
    previousOverflow = openDialogs.length ? '' : doc.body.style.overflow;
    openDialogs.forEach(other => { if (other !== dialog) other.close(); });
    trigger = link;
    player = doc.createElement('video');
    player.controls = true;
    player.playsInline = true;
    player.preload = 'none';
    player.setAttribute('playsinline', '');
    player.setAttribute('preload', 'none');
    player.setAttribute('aria-label', link.dataset.fabLabel || 'Fill-A-Bag');
    const poster = shopifyEventMediaUrl(link.dataset.fabPoster, win.location.href);
    if (poster) player.poster = poster;
    if (Number(link.dataset.fabWidth) > 0) player.width = Number(link.dataset.fabWidth);
    if (Number(link.dataset.fabHeight) > 0) player.height = Number(link.dataset.fabHeight);
    playerError = () => { if (error) error.hidden = false; };
    player.addEventListener('error', playerError);
    player.src = src;
    content.replaceChildren(player);
    if (fallback) fallback.href = src;
    try { dialog.showModal(); }
    catch { releasePlayer(); return false; }
    opened = true; activeGallery = api;
    trigger.setAttribute('aria-expanded', 'true');
    doc.body.style.overflow = 'hidden';
    closeButton?.focus({ preventScroll: true });
    return true;
  }
  function finishDrag(event, cancelled = false) {
    if (!drag || (event?.pointerId !== undefined && event.pointerId !== drag.id)) return;
    const state = drag; drag = undefined;
    track.classList.remove('is-dragging');
    if (track.hasPointerCapture?.(state.id)) track.releasePointerCapture(state.id);
    if (state.moved && !cancelled) {
      suppressClick = true;
      win.clearTimeout(clickTimer);
      clickTimer = win.setTimeout(() => { suppressClick = false; }, 0);
    }
  }
  listen(track, 'pointerdown', event => {
    if (event.pointerType !== 'mouse' || event.button !== 0 || event.isPrimary === false || maximum() <= 2) return;
    drag = { id: event.pointerId, startX: event.clientX, startScroll: track.scrollLeft, moved: false };
  });
  listen(win, 'pointermove', event => {
    if (!drag || event.pointerId !== drag.id) return;
    const distance = event.clientX - drag.startX;
    if (!drag.moved && Math.abs(distance) < 6) return;
    if (!drag.moved) { drag.moved = true; track.classList.add('is-dragging'); track.setPointerCapture?.(drag.id); }
    event.preventDefault();
    track.scrollLeft = Math.max(0, Math.min(maximum(), drag.startScroll - distance));
    updateArrows();
  }, { passive: false });
  listen(win, 'pointerup', event => finishDrag(event));
  listen(win, 'pointercancel', event => finishDrag(event, true));
  listen(track, 'lostpointercapture', event => finishDrag(event, true));
  listen(track, 'dragstart', event => event.preventDefault());
  listen(track, 'click', event => {
    if (suppressClick && event.detail !== 0) { suppressClick = false; event.preventDefault(); event.stopPropagation(); }
  }, { capture: true });
  listen(track, 'click', event => {
    const link = event.target.closest?.('[data-fab-open]');
    if (!link || !track.contains(link) || event.defaultPrevented || (event.button !== undefined && event.button !== 0) || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    event.preventDefault(); open(link);
  });
  listen(previous, 'click', () => move(-1));
  listen(next, 'click', () => move(1));
  listen(track, 'scroll', updateArrows, { passive: true });
  listen(win, 'resize', updateArrows, { passive: true });
  listen(track, 'keydown', event => {
    if (event.target !== track || !['Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    track.scrollTo({ left: event.key === 'Home' ? 0 : maximum(), behavior: reducedMotion.matches ? 'auto' : 'smooth' });
  });
  listen(closeButton, 'click', close);
  listen(dialog, 'close', finishClose);
  listen(dialog, 'cancel', event => { event.preventDefault(); close(); });
  listen(dialog, 'click', event => {
    if (event.target !== dialog) return;
    const bounds = dialog.getBoundingClientRect();
    if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) close();
  });
  listen(doc, 'visibilitychange', () => { if (doc.hidden) player?.pause(); });
  listen(win, 'pagehide', close);
  const observer = win.ResizeObserver ? new win.ResizeObserver(updateArrows) : null;
  observer?.observe(track);
  for (const image of track.querySelectorAll('img')) {
    const failed = () => { image.hidden = true; image.closest('[data-fab-open]')?.classList.add('is-missing-poster'); };
    listen(image, 'error', failed, { once: true });
    if (image.complete && image.naturalWidth === 0) failed();
  }
  const api = { open, close, updateArrows, destroy() {
    if (destroyed) return;
    close(); destroyed = true; finishDrag(undefined, true); suppressClick = false;
    win.clearTimeout(clickTimer); observer?.disconnect(); controller.abort(); galleries.delete(section);
  } };
  galleries.set(section, api); updateArrows();
  return api;
}

export function initFillBagMedia(root) {
  if (!root?.querySelectorAll) return;
  const heroNodes = [...root.querySelectorAll('[data-fab-hero-image]')];
  if (root.matches?.('[data-fab-hero-image]')) heroNodes.unshift(root);
  for (const image of heroNodes) {
    if (heroImages.has(image)) continue;
    heroImages.add(image);
    const failed = () => {
      const hero = image.closest('[data-fab-hero]');
      const frame = hero?.querySelector('[data-fab-hero-frame]');
      const fallback = hero?.querySelector('[data-fab-hero-fallback]');
      if (frame) frame.hidden = true;
      if (fallback) fallback.hidden = false;
      hero?.classList.remove('fab-hero--photo');
    };
    image.addEventListener('error', failed, { once: true });
    if (image.complete && image.naturalWidth === 0) failed();
  }
  const nodes = [...root.querySelectorAll('[data-fab-gallery]')];
  if (root.matches?.('[data-fab-gallery]')) nodes.unshift(root);
  for (const section of nodes) mountFillBagGallery(section);
}
if (typeof document !== 'undefined') {
  initFillBagMedia(document);
  document.addEventListener('shopify:section:load', event => initFillBagMedia(event.target));
  document.addEventListener('shopify:section:unload', event => {
    const root = event.target;
    if (root.matches?.('[data-fab-gallery]')) galleries.get(root)?.destroy();
    root.querySelectorAll?.('[data-fab-gallery]').forEach(section => galleries.get(section)?.destroy());
  });
}
